import React, { useEffect } from 'react';
import { useAppStore } from '../stores/appStore';
import { useMatchStore } from '../stores/matchStore';
import { useQueueStore } from '../stores/queueStore';
import { QueueDisplay, RotationControl } from '../components/Queue';
import { OverallStats } from '../components/Statistics';
import { 
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

interface DashboardProps {
  onNavigate?: (page: string) => void;
}

const Dashboard: React.FC<DashboardProps> = ({ onNavigate }) => {
  const { isInitialized, isInitializing, initializeApp } = useAppStore();

  useEffect(() => {
    if (!isInitialized && !isInitializing) {
      initializeApp();
    }
  }, [isInitialized, isInitializing, initializeApp]);

  if (isInitializing) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">系统初始化中</h2>
          <p className="text-gray-600">正在加载数据和配置...</p>
        </div>
      </div>
    );
  }

  if (!isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="h-12 w-12 text-red-600 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-800 mb-2">初始化失败</h2>
          <p className="text-gray-600 mb-4">系统无法正常启动，请检查配置</p>
          <button
            onClick={initializeApp}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg"
          >
            重试初始化
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">仪表盘</h1>
            <p className="text-gray-600 mt-1">系统概览和快速操作</p>
          </div>
          <div className="flex items-center space-x-2 text-green-600">
            <CheckCircleIcon className="w-5 h-5" />
            <span className="text-sm font-medium">系统运行正常</span>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column */}
        <div className="space-y-6">
          {/* Queue Display */}
          <QueueDisplay />
          
          {/* Rotation Control */}
          <RotationControl />
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Match Status Display */}
          <MatchStatusDisplay onNavigate={onNavigate} />
          
          {/* Quick Pairing Preview */}
          <QuickPairingPreview onNavigate={onNavigate} />
          
          {/* Quick Stats */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">快速统计</h3>
            <OverallStats />
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">最近活动</h3>
        <div className="space-y-3">
          <ActivityItem
            icon={<ClockIcon className="w-5 h-5 text-blue-600" />}
            title="系统启动"
            description="轮换比赛管理系统已成功初始化"
            time="刚刚"
          />
          {/* 这里可以添加更多活动记录 */}
        </div>
      </div>
    </div>
  );
};

interface ActivityItemProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  time: string;
}

const ActivityItem: React.FC<ActivityItemProps> = ({ icon, title, description, time }) => {
  return (
    <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
      <div className="flex-shrink-0">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900">{title}</p>
        <p className="text-sm text-gray-600">{description}</p>
      </div>
      <div className="flex-shrink-0 text-xs text-gray-500">
        {time}
      </div>
    </div>
  );
};

// 比赛状态显示组件
const MatchStatusDisplay: React.FC<{ onNavigate?: (page: string) => void }> = ({ onNavigate }) => {
  const { inProgressMatch, loadInProgressMatch } = useMatchStore();

  useEffect(() => {
    loadInProgressMatch();
  }, [loadInProgressMatch]);

  if (!inProgressMatch) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-800">比赛进行中</h3>
        <span className="px-3 py-1 bg-green-100 text-green-800 text-sm font-medium rounded-full">
          进行中
        </span>
      </div>

      <div className="bg-gradient-to-r from-blue-50 to-green-50 rounded-lg p-6 mb-4">
        <div className="flex items-center justify-center space-x-8">
          {/* 队伍1 */}
          <div className="text-center">
            <div className="bg-blue-100 border-2 border-blue-300 rounded-lg p-4 min-w-[120px]">
              <div className="text-blue-800 font-semibold mb-2">开室</div>
              <div className="space-y-1">
                {inProgressMatch.pairing.team1.map((player) => (
                  <div key={player.id} className="text-sm">
                    <div className="font-medium">{player.name}</div>
                    <div className="text-xs text-blue-600">#{player.number}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* VS */}
          <div className="text-2xl font-bold text-gray-400">🤝</div>

          {/* 队伍2 */}
          <div className="text-center">
            <div className="bg-green-100 border-2 border-green-300 rounded-lg p-4 min-w-[120px]">
              <div className="text-green-800 font-semibold mb-2">闭室</div>
              <div className="space-y-1">
                {inProgressMatch.pairing.team2.map((player) => (
                  <div key={player.id} className="text-sm">
                    <div className="font-medium">{player.name}</div>
                    <div className="text-xs text-green-600">#{player.number}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="text-center">
        <button
          onClick={() => onNavigate?.('match')}
          className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded-lg transition-colors"
        >
          录入比赛结果
        </button>
      </div>

      <div className="mt-4 text-center text-sm text-gray-500">
        比赛开始时间：{new Date(inProgressMatch.playedAt).toLocaleString('zh-CN')}
      </div>

      <div className="mt-4 p-3 bg-yellow-50 rounded-lg">
        <p className="text-sm text-yellow-800">
          💡 <strong>提示：</strong>生成配对后点击"开始比赛"，比赛进行中所有人都能看到配对信息。比赛结束后点击"录入比赛结果"保存结果。
        </p>
      </div>
    </div>
  );
};

// 快速配对预览组件
const QuickPairingPreview: React.FC<{ onNavigate?: (page: string) => void }> = ({ onNavigate }) => {
  const { currentPlayers, canStartMatch } = useQueueStore();
  const { generatePairing, currentPairing, inProgressMatch } = useMatchStore();

  const handleQuickPairing = () => {
    if (canStartMatch()) {
      generatePairing(currentPlayers);
    }
  };

  // 如果有进行中的比赛，不显示配对预览
  if (inProgressMatch) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-800">快速配对预览</h3>
        <button
          onClick={() => onNavigate?.('match')}
          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
        >
          进入比赛管理 →
        </button>
      </div>

      {!canStartMatch() ? (
        <div className="text-center py-8 text-gray-500">
          <p className="mb-2">需要至少4个参赛者才能生成配对</p>
          <p className="text-sm">当前参赛者: {currentPlayers.length}/4</p>
        </div>
      ) : currentPairing ? (
        <div>
          <div className="bg-gradient-to-r from-blue-50 to-green-50 rounded-lg p-6 mb-4">
            <div className="flex items-center justify-center space-x-8">
              {/* 队伍1 */}
              <div className="text-center">
                <div className="bg-blue-100 border-2 border-blue-300 rounded-lg p-4">
                  <div className="text-blue-800 font-semibold mb-2">开室</div>
                  <div className="space-y-1">
                    {currentPairing.team1.map((player) => (
                      <div key={player.id} className="text-sm">
                        <div className="font-medium">{player.name}</div>
                        <div className="text-xs text-blue-600">#{player.number}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* VS */}
              <div className="text-2xl font-bold text-gray-400">🤝</div>

              {/* 队伍2 */}
              <div className="text-center">
                <div className="bg-green-100 border-2 border-green-300 rounded-lg p-4">
                  <div className="text-green-800 font-semibold mb-2">闭室</div>
                  <div className="space-y-1">
                    {currentPairing.team2.map((player) => (
                      <div key={player.id} className="text-sm">
                        <div className="font-medium">{player.name}</div>
                        <div className="text-xs text-green-600">#{player.number}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center">
            <button
              onClick={handleQuickPairing}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded-lg transition-colors inline-flex items-center space-x-2"
            >
              <ArrowPathIcon className="w-5 h-5" />
              <span>重新配对</span>
            </button>
          </div>
        </div>
      ) : (
        <div>
          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <h4 className="font-semibold text-gray-700 mb-3">当前参赛者</h4>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {currentPlayers.map((player) => (
                <div
                  key={player.id}
                  className="bg-white border border-gray-200 rounded-lg p-2 text-center"
                >
                  <div className="font-semibold text-gray-800">{player.name}</div>
                  <div className="text-xs text-gray-500">#{player.number}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="text-center">
            <button
              onClick={handleQuickPairing}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded-lg transition-colors inline-flex items-center space-x-2"
            >
              <ArrowPathIcon className="w-5 h-5" />
              <span>随机配对</span>
            </button>
          </div>
        </div>
      )}

      <div className="mt-4 p-3 bg-blue-50 rounded-lg">
        <p className="text-sm text-blue-800">
          💡 要开始比赛，请点击左侧导航的"比赛管理"页面
        </p>
      </div>
    </div>
  );
};

export default Dashboard;